import * as z from 'zod/mini';

export enum StudentViewMode {
  Basic = 'ael', //online
  Preview = 'preview', //online
  Offline = 'offline',
  SCORM = 'scorm', // online
  Public = 'public', // online
  Offline_SCORM = 'scorm_custom_hosted',
  TinCan = 'tincan', // does not exists yet
  Offline_TinCan = 'offline_tincan',
  ExportedPackaged_NullClient = 'exportedPackaged_NullClient',
}

export type OnlineStudentViewModes =
  | StudentViewMode.Basic
  | StudentViewMode.Public
  | StudentViewMode.SCORM
  | StudentViewMode.Preview;
export type OfflineStudentViewModes =
  | StudentViewMode.Offline
  | StudentViewMode.Offline_SCORM
  | StudentViewMode.TinCan
  | StudentViewMode.Offline_TinCan
  | StudentViewMode.ExportedPackaged_NullClient;

/**
 * Which of the two startup paths a view mode belongs to. Typed as a full `Record`, so adding a view
 * mode to the enum without classifying it here is a compile error.
 */
const VIEW_MODE_KIND: Record<StudentViewMode, 'online' | 'offline'> = {
  [StudentViewMode.Basic]: 'online',
  [StudentViewMode.Preview]: 'online',
  [StudentViewMode.SCORM]: 'online',
  [StudentViewMode.Public]: 'online',
  [StudentViewMode.Offline]: 'offline',
  [StudentViewMode.Offline_SCORM]: 'offline',
  [StudentViewMode.TinCan]: 'offline',
  [StudentViewMode.Offline_TinCan]: 'offline',
  [StudentViewMode.ExportedPackaged_NullClient]: 'offline',
};

/** Course content and tracking come from the exported package, not from the backend. */
export const isOfflineViewMode = (viewMode: StudentViewMode): viewMode is OfflineStudentViewModes =>
  VIEW_MODE_KIND[viewMode] === 'offline';

export const isOnlineViewMode = (viewMode: StudentViewMode): viewMode is OnlineStudentViewModes =>
  VIEW_MODE_KIND[viewMode] === 'online';

/**
 * Accepts the enum values (`ael`, `scorm_custom_hosted`, ...), rejects anything else. The message is
 * spelled out because `zod/mini` ships without the default message locale.
 */
export const studentViewModeSchema = z.enum(StudentViewMode, {
  error: `VIEW_MODE is missing or unknown, expected one of: ${Object.values(StudentViewMode).join(', ')}.`,
});

export const getStudentViewModeFromString = (value: string): StudentViewMode => {
  const result = studentViewModeSchema.safeParse(value);
  if (!result.success) {
    throw new Error(`${z.prettifyError(result.error)} Got: '${value}'.`);
  }

  return result.data;
};

export const assertNeverForViewMode = (viewMode: never): never => {
  throw new Error('Unknown StudentViewMode: ' + viewMode);
};
